import { useState, useCallback, useRef, useEffect } from 'react';
import { getSnapshot } from '../supabase/snapshotService';
import { logger } from '../utils/logger';

interface WeekCache {
  [weekKey: string]: {
    [section: string]: any;
    timestamp: number;
  };
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 10; // Maximum number of weeks to cache

/**
 * Week switching cache hook (Phase 2)
 * 
 * Features:
 * - Caches snapshot data for previous/next weeks
 * - Prefetches adjacent weeks on hover
 * - Optimizes week switch transitions
 * - Auto-expires old cache entries
 * 
 * Usage:
 * ```tsx
 * const { getCachedWeek, prefetchWeek, clearCache } = useWeekCache(workspaceId);
 * 
 * // Prefetch next week on hover
 * <button onMouseEnter={() => prefetchWeek('2025-W38', 'liveAssets')}>
 *   Next Week
 * </button>
 * ```
 */
export function useWeekCache(workspaceId: string | null) {
  const cacheRef = useRef<WeekCache>({});
  const [, forceUpdate] = useState(0);

  // Clear expired cache entries
  const clearExpired = useCallback(() => {
    const now = Date.now();
    const cache = cacheRef.current;
    
    Object.keys(cache).forEach(weekKey => {
      if (now - cache[weekKey].timestamp > CACHE_DURATION) {
        delete cache[weekKey];
      }
    });
    
    // Limit cache size
    const entries = Object.entries(cache);
    if (entries.length > MAX_CACHE_SIZE) {
      // Sort by timestamp and remove oldest
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, entries.length - MAX_CACHE_SIZE);
      toRemove.forEach(([weekKey]) => {
        delete cache[weekKey];
      });
    }
  }, []);

  // Get cached data for a week
  const getCachedWeek = useCallback((weekKey: string, section: string): any | null => {
    clearExpired();
    const cached = cacheRef.current[weekKey];
    if (!cached || !cached[section]) return null;
    
    // Check if cache is still valid
    if (Date.now() - cached.timestamp > CACHE_DURATION) {
      delete cacheRef.current[weekKey];
      return null;
    }
    
    return cached[section];
  }, [clearExpired]);

  // Prefetch week data
  const prefetchWeek = useCallback(async (weekKey: string, section: string): Promise<void> => {
    if (!workspaceId || !weekKey) return;
    
    // Check if already cached
    const cached = getCachedWeek(weekKey, section);
    if (cached) return;
    
    // Check if already prefetching
    const cache = cacheRef.current[weekKey];
    if (cache && cache[`_prefetching_${section}`]) return;
    
    // Mark as prefetching
    if (!cache) {
      cacheRef.current[weekKey] = { timestamp: Date.now() };
    }
    cacheRef.current[weekKey][`_prefetching_${section}`] = true;
    
    try {
      const result = await getSnapshot(weekKey, section, workspaceId);
      if (result && result.rowExists) {
        const data = result.data;
        if (!cacheRef.current[weekKey]) {
          cacheRef.current[weekKey] = { timestamp: Date.now() };
        }
        cacheRef.current[weekKey][section] = data;
        delete cacheRef.current[weekKey][`_prefetching_${section}`];
        forceUpdate(prev => prev + 1);
      }
    } catch (error) {
      delete cacheRef.current[weekKey]?.[`_prefetching_${section}`];
      if (import.meta.env.DEV) {
        logger.warn(`Prefetch failed for ${weekKey}/${section}`, { error }, 'useWeekCache');
      }
    }
  }, [workspaceId, getCachedWeek]);

  // Prefetch adjacent weeks
  const prefetchAdjacentWeeks = useCallback((currentWeek: string, section: string): void => {
    if (!workspaceId || !currentWeek) return;
    
    // Parse week key (format: YYYY-WNN)
    const match = currentWeek.match(/^(\d{4})-W(\d{2})$/);
    if (!match) return;
    
    const year = parseInt(match[1], 10);
    const week = parseInt(match[2], 10);
    
    // Prefetch previous week
    let prevWeek = week - 1;
    let prevYear = year;
    if (prevWeek < 1) {
      prevWeek = 52;
      prevYear = year - 1;
    }
    const prevWeekKey = `${prevYear}-W${prevWeek.toString().padStart(2, '0')}`;
    prefetchWeek(prevWeekKey, section);
    
    // Prefetch next week
    let nextWeek = week + 1;
    let nextYear = year;
    if (nextWeek > 52) {
      nextWeek = 1;
      nextYear = year + 1;
    }
    const nextWeekKey = `${nextYear}-W${nextWeek.toString().padStart(2, '0')}`;
    prefetchWeek(nextWeekKey, section);
  }, [workspaceId, prefetchWeek]);

  // Clear entire cache
  const clearCache = useCallback(() => {
    cacheRef.current = {};
    forceUpdate(prev => prev + 1);
  }, []);

  // Clear expired entries periodically
  useEffect(() => {
    const interval = setInterval(clearExpired, 60000); // Every minute
    return () => clearInterval(interval);
  }, [clearExpired]);

  return {
    getCachedWeek,
    prefetchWeek,
    prefetchAdjacentWeeks,
    clearCache
  };
}

